import styled from "@emotion/styled";
import type { FC } from "react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useBloc } from "@blac/react";
import InDialog from "src/ui/components/InDialog/InDialog";
import {
  AppQueryPopupsController,
  AppPopup
} from "src/ui/components/AppQueryPopups/AppQueryPopupsBloc";
import useGoToOrBack from "src/ui/hooks/useGoToOrBack";
import translate from "src/lib/translate";
import { IconClose } from "src/constants/icons";
import RefillRequirementsCubit from "src/state/RefillRequirementsCubit/RefillRequirementsCubit";
import {
  REFILL_URGENT_THRESHOLD_DAYS,
  isRenewalOverdue,
  getDaysOverdue,
  getOverdueFraction
} from "src/state/RefillRequirementsCubit/RefillRequirementsHelpers";
import { RefillCohort } from "src/state/RefillRequirementsCubit/RefillRequirementsTypes";
import { getRefillCohort, isHwjMember } from "src/lib/refillCohort";
import { useBloc as useAppBloc, tracker, userState } from "src/state/state";
import { TrackEvent, TrackType } from "src/state/Track/TrackCubit";
import { APP_CHAT_PATH } from "src/lib/createAppPath";
import PatientPharmacyBloc from "src/state/PatientPharmacyBloc/PatientPharmacyBloc";
import { PayerId, EmployerId } from "src/constants/payers";
import PharmacyInformationDetail from "../../../PharmacyInformationDetail/PharmacyInformationDetail";
import { ArticleCard } from "src/ui/components/ArticleCard/ArticleCard";
import {
  REFILL_ARTICLES_CONFIG,
  REFILL_REQUIREMENTS_ARTICLE_URLS,
  REFILL_REQUIREMENTS_ARTICLE_IMAGE,
  MANAGING_SIDE_EFFECTS_ARTICLE_IMAGE
} from "src/constants/refillArticles";
import RefillHubHeader from "./RefillHubHeader";
import RefillHubProgress from "./RefillHubProgress";
import RefillRequirementsChecklist from "./RefillRequirementsChecklist";
import HubSectionLabel from "./HubSectionLabel";
import RefillHubAssistantCta from "./RefillHubAssistantCta";
import type { CountdownTone } from "./CountdownRing";

/** Requirements block; pulled up to overlap the hero's reserved bottom padding so the
 * progress sits over the photo and the list card overlaps the hero edge (Figma -80px). */
const Requirements = styled.div`
  position: relative;
  z-index: 1;
  margin-top: -80px;
  display: flex;
  flex-direction: column;
  /* No gap here — Figma separates the progress and list only by the progress's own
     12px bottom padding; an extra gap reduces the overlap with the hero. */
  gap: 0;
  padding: 0 1.5rem;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  /* 24px horizontal to match the Requirements block above, so the pharmacy card and article
     grid share the requirements list's content column. */
  padding: 1.25rem 1.5rem;

  /* No clearance needed for the CTA bar below: it is sticky, so it stays in normal flow and
     reserves its own height at the end of the scroll — nothing ends up permanently behind it.
     This is only the gap between the last article card and the bar, and it keeps the
     specificity override in case InDialog's last-child rule ever applies to Body again. */
  &&& {
    padding-bottom: 1.5rem;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

/** Zero-height sticky wrapper: pins the close button to the top of the scrolling dialog
 * viewport so it floats over the content and stays visible while scrolling, without
 * reserving layout space (the hero still starts at the top). z-index keeps it above the
 * overlapping requirements card. */
const CloseBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  height: 0;
`;

/** Frosted translucent close button per Figma (Medication Experience – v1, node
 * 5805:93775): a 12% charcoal fill over a 25px backdrop blur, not a solid white disc. */
const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 2;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(33, 33, 33, 0.12);
  backdrop-filter: blur(25px);
  -webkit-backdrop-filter: blur(25px);
  border: none;
  cursor: pointer;

  /* IconClose hardcodes its stroke (#212121); recolor to white for the frosted button. */
  svg path {
    stroke: var(--color-white);
  }
`;

/** Related-materials grid per Figma (Medication Experience – v1, node 5805:93599): a 2-up
 * grid of the shared ArticleCard (image + frosted title bar). Per FEWEB-2503 the hub shows
 * two articles — side effects + smooth prescription renewal. */
const ArticleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  /* Drop the shared ArticleCard's 9.75rem cap so each card fills its 1fr column; otherwise
     it left-aligns and the right card stays short of the content edge. */
  & > button {
    max-width: none;
  }

  /* Allow 3 title lines (Figma's 60px title area); the shared card clamps at 2 and truncates
     our titles. Overrides from here, not the shared component. */
  & > button span {
    -webkit-line-clamp: 3;
    line-clamp: 3;
  }

  /* Desktop columns are wide — read the stretched cards as square (Figma node 5805:93878). */
  @media (min-width: 40rem) {
    & > button {
      height: auto;
      aspect-ratio: 1;
    }
  }
`;

/**
 * URL for the payer-specific "How to ensure a smooth prescription renewal" article.
 * Mirrors the legacy hub, but resolves the HWJ case from `experienceGroup` (via
 * `isHwjMember`) instead of the consistency score, which the decoupled hub doesn't load.
 */
const getRefillRequirementsArticleUrl = (): string => {
  if (userState.isPayerMember(PayerId.EVERNORTH)) {
    return REFILL_REQUIREMENTS_ARTICLE_URLS.EVERNORTH;
  }
  if (userState.isEmployerMember(EmployerId.BANK_OF_NEW_YORK)) {
    return REFILL_REQUIREMENTS_ARTICLE_URLS.BNY;
  }
  if (isHwjMember()) {
    return REFILL_REQUIREMENTS_ARTICLE_URLS.HWJ;
  }
  return REFILL_REQUIREMENTS_ARTICLE_URLS.NON_HWJ;
};

const PrepareRefillDialogV2: FC<{ returnUrl: string }> = ({ returnUrl }) => {
  const [
    { data },
    { getProgress, getDaysUntilNextRefill, getCountdownFraction, refresh }
  ] = useBloc(RefillRequirementsCubit);

  // Re-fetch each time the hub opens so progress reflects actions taken elsewhere
  // (e.g. a weigh-in) since the keepAlive cubit last loaded.
  useEffect(() => {
    refresh();
  }, [refresh]);

  const [{ preferredPharmacy }, { loadPreferredPharmacy }] =
    useAppBloc(PatientPharmacyBloc);

  // Load the preferred pharmacy up front so the pharmacy section (its header + card) only
  // renders when the member actually has one on file.
  useEffect(() => {
    void loadPreferredPharmacy();
  }, [loadPreferredPharmacy]);

  const navigate = useGoToOrBack();
  // Chat is a forward navigation, not a return to where the hub was opened from, so it uses the
  // plain router navigate rather than the hub's returnUrl-aware one.
  const routerNavigate = useNavigate();

  const onClose = () => {
    AppQueryPopupsController.closePopup();
    navigate(returnUrl, { multiBack: true, replace: true });
  };

  // Leaves the member on a blank composer (or their most recent conversation) — the hub passes
  // no context, so nothing is pre-filled or sent on their behalf.
  const openAssistant = () => {
    tracker.track(TrackEvent.SophieChatEntryPoint, {
      type: TrackType.click,
      data: { source: "refillHub" }
    });
    AppQueryPopupsController.closePopup();
    routerNavigate(APP_CHAT_PATH);
  };

  // Open the refill survey from the actionable check-in row; returns to the same returnUrl
  // as the hub (mirrors the legacy dialog's survey entry point).
  const openRefillSurvey = () => {
    AppQueryPopupsController.openPopup(AppPopup.glp1Refill, {
      additionalParameters: { returnUrl }
    });
  };

  const isConcierge = getRefillCohort() === RefillCohort.THROUGH_9AM;

  const medicationName = data?.medicationName ?? undefined;

  const title = !isConcierge
    ? translate("prepareRefillDialog.v2.title.secure")
    : medicationName
      ? translate("prepareRefillDialog.v2.title.refillMedication", {
          medication: medicationName
        })
      : translate("prepareRefillDialog.v2.title.refill");

  const requirements = data?.requirements ?? [];
  const progress = getProgress();
  const overdue = data ? isRenewalOverdue(data) : false;
  // Overdue: fill the ring red on the same 14-day scale (half at 7 overdue); otherwise count
  // down green/orange.
  const days = overdue ? getDaysOverdue(data?.dueAt) : getDaysUntilNextRefill();
  const fraction =
    overdue && data ? getOverdueFraction(data) : getCountdownFraction();
  const tone: CountdownTone = overdue
    ? "red"
    : days <= REFILL_URGENT_THRESHOLD_DAYS
      ? "orange"
      : "green";

  const articles = [
    {
      title: translate(
        "prepareRefillDialog.articles.managingSideEffects.title"
      ),
      url: REFILL_ARTICLES_CONFIG.managingSideEffects.url,
      imageSrc: MANAGING_SIDE_EFFECTS_ARTICLE_IMAGE
    },
    {
      title: translate("prepareRefillDialog.articles.refillRequirements.title"),
      url: getRefillRequirementsArticleUrl(),
      imageSrc: REFILL_REQUIREMENTS_ARTICLE_IMAGE
    }
  ];

  return (
    <InDialog
      hideHeader
      noPadding
      onClose={onClose}
      returnUrl={returnUrl}
      width="min(calc(100% - 2rem), 38rem)"
      // Fill the viewport height (minus a 2rem inset), capped at 70rem — the shared dialog
      // idiom (GLP1RefillDialog, AppointmentDialog, …). Applies on desktop/floating only
      // (InDialog gates height on ≥600px); mobile stays a full-height sheet.
      height="min(calc(100% - 4rem), 70rem)"
    >
      <CloseBar>
        <CloseButton
          onClick={onClose}
          aria-label={translate("prepareRefillDialog.v2.close")}
        >
          <IconClose />
        </CloseButton>
      </CloseBar>

      <RefillHubHeader
        title={title}
        days={days}
        fraction={fraction}
        tone={tone}
        overdue={overdue}
        showCountdown={isConcierge}
      />

      <Requirements>
        <RefillHubProgress progress={progress} />
        <RefillRequirementsChecklist
          requirements={requirements}
          status={data?.status}
          onOpenSurvey={openRefillSurvey}
        />
      </Requirements>

      <Body>
        {/* Concierge-only (renewal through 9am) and gated on a pharmacy being on file — so
            the section is hidden for non-concierge members and never shown with an empty
            "Your pharmacy" header. hideTitle suppresses the component's own title since the
            section header provides it (Figma renders the section header, not the card's title). */}
        {isConcierge && preferredPharmacy && (
          <Section>
            <HubSectionLabel>
              {translate("prepareRefillDialog.v2.pharmacy.label")}
            </HubSectionLabel>
            <PharmacyInformationDetail
              compact
              hideTitle
              descriptionText={translate(
                "prepareRefillDialog.pharmacy.description"
              )}
            />
          </Section>
        )}

        <Section>
          <HubSectionLabel>
            {translate("prepareRefillDialog.relatedMaterials.title")}
          </HubSectionLabel>
          <ArticleGrid>
            {articles.map((article) => (
              <ArticleCard
                key={article.title}
                title={article.title}
                url={article.url}
                image={article.imageSrc}
                imageAlt={article.title}
              />
            ))}
          </ArticleGrid>
        </Section>
      </Body>

      {/* Direct child of InDialog's scroll container so `position: sticky` pins it to the
          scrollport — nesting it alongside the content would let it scroll away. */}
      <RefillHubAssistantCta onPress={openAssistant} />
    </InDialog>
  );
};

export default PrepareRefillDialogV2;
